import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Circle, Loader2, Sparkles, PlayCircle, Clock } from 'lucide-react';
import { warn } from '../../utils/logger';
export type MasteryStatus = 'starten' | 'bezig' | 'bijna_klaar' | 'beheerst';
/** Optional decay status from masteryDecay.ts */
export type DecayStatus = 'fresh' | 'recent' | 'fading' | 'stale';
interface MasteryBadgeBaseProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Optional decay indicator — shows when mastery is fading or stale */
  decayStatus?: DecayStatus;
}
interface MasteryBadgeStatusProps extends MasteryBadgeBaseProps {
  status: MasteryStatus;
  percentage?: never;
}
interface MasteryBadgePercentageProps extends MasteryBadgeBaseProps {
  percentage: number;
  status?: never;
}
type MasteryBadgeProps = MasteryBadgeStatusProps | MasteryBadgePercentageProps;
/**
 * Derive MasteryStatus from a percentage score.
 * Thresholds aligned with masteryCanonical.ts:
 * - 0 → starten
 * - 1-59 → bezig
 * - 60-94 → bijna_klaar
 * - 95+ → beheerst
 */
function statusFromPercentage(pct: number): MasteryStatus {
  if (pct <= 0) return 'starten';
  if (pct < 60) return 'bezig';
  if (pct < 95) return 'bijna_klaar';
  return 'beheerst';
}
const STATUS_CONFIG: Record<MasteryStatus, {
  label: string;
  icon: React.ElementType;
  color: string;
  bg: string;
  border: string;
  ariaLabel: string;
}> = {
  starten: {
    label: 'Starten',
    icon: PlayCircle,
    color: 'text-slate-500',
    bg: 'bg-slate-100',
    border: 'border-slate-200',
    ariaLabel: 'Nog niet begonnen'
  },
  bezig: {
    label: 'Bezig',
    icon: Loader2,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    border: 'border-indigo-100',
    ariaLabel: 'Mee bezig'
  },
  bijna_klaar: {
    label: 'Bijna klaar',
    icon: Sparkles,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    ariaLabel: 'Bijna beheerst'
  },
  beheerst: {
    label: 'Beheerst',
    icon: CheckCircle,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    ariaLabel: 'Volledig beheerst'
  }
};
const SIZE_CLASSES = {
  sm: {
    container: 'px-2 py-0.5',
    icon: 'w-3 h-3',
    text: 'text-[9px]',
    pctText: 'text-[9px]'
  },
  md: {
    container: 'px-3 py-1',
    icon: 'w-3.5 h-3.5',
    text: 'text-[10px]',
    pctText: 'text-[10px]'
  },
  lg: {
    container: 'px-4 py-1.5',
    icon: 'w-4 h-4',
    text: 'text-xs',
    pctText: 'text-xs'
  }
};
export function MasteryBadge(props: MasteryBadgeProps) {
  const {
    className = '',
    size = 'md',
    decayStatus
  } = props;
  // Resolve status from either explicit status or percentage
  let resolvedStatus: MasteryStatus;
  let showPercentage = false;
  let percentage = 0;
  if ('percentage' in props && props.percentage !== undefined) {
    percentage = props.percentage;
    resolvedStatus = statusFromPercentage(percentage);
    showPercentage = true;
  } else if ('status' in props && props.status !== undefined) {
    resolvedStatus = props.status;
  } else {
    resolvedStatus = 'starten';
  }
  // Defensive: fallback to 'starten' if invalid status
  const safeStatus: MasteryStatus = STATUS_CONFIG[resolvedStatus] ? resolvedStatus : 'starten';
  if (process.env.NODE_ENV === 'development' && !STATUS_CONFIG[resolvedStatus]) {
    warn(`MasteryBadge: Invalid status "${resolvedStatus}", falling back to "starten"`);
  }
  const {
    label,
    icon: Icon,
    color,
    bg,
    border,
    ariaLabel
  } = STATUS_CONFIG[safeStatus];
  const sizeClasses = SIZE_CLASSES[size];
  // Decay indicator: show a subtle clock icon for fading/stale mastery
  const showDecay = decayStatus === 'fading' || decayStatus === 'stale';
  // Build accessible label
  const fullAriaLabel = showPercentage ? `${ariaLabel}: ${percentage}%${showDecay ? ' (niet recent geoefend)' : ''}` : `${ariaLabel}${showDecay ? ' (niet recent geoefend)' : ''}`;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} role="status" aria-label={fullAriaLabel} className={`
        inline-flex items-center gap-1.5 ${sizeClasses.container} rounded-full border shadow-sm
        ${bg} ${border} ${className}
      `} data-id="element-4936">
      <Icon className={`${sizeClasses.icon} ${color}`} strokeWidth={2.5} data-id="element-4937" />

      {showPercentage ? <span className={`${sizeClasses.pctText} font-black ${color}`} data-id="element-4938">
          {percentage}%
        </span> : <span className={`${sizeClasses.text} font-black uppercase tracking-wider ${color}`} data-id="element-4939">
          {label}
        </span>}

      {showDecay && <Clock className={`${sizeClasses.icon} text-slate-400 -ml-0.5`} strokeWidth={2} aria-hidden="true" data-id="element-4940" />}
    </motion.div>;
}